/**
 * Ad-hoc A/B benchmark report: per-arm aggregate metrics, the override deltas
 * that define each arm, and a per-flow breakdown across arms.
 *
 * @module pages/ArmComparisonReport
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Box, Card, CardContent, Flex, Heading, Text, Badge, LoadingOrbit } from '@chatsdet/ui';
import {
  useArmReportQuery,
  type ArmComparisonRow,
  type BenchmarkArm,
} from '../useBenchmarkWorkflowData';
import {
  formatDuration,
  formatTokenCount,
  stripBenchmarkPrefix,
} from '../workflow-suites/useWorkflowSuiteTransforms';

type ArmAggregate = {
  arm: string;
  runs: number;
  passed: number;
  avgDurationMs: number | null;
  avgTokens: number | null;
  cacheHits: number;
  l2Recoveries: number;
  ejections: number;
  agentSteps: number;
};

function aggregateByArm(rows: ArmComparisonRow[]): ArmAggregate[] {
  const byArm = new Map<string, ArmComparisonRow[]>();
  for (const r of rows) {
    const key = r.arm || 'baseline';
    const list = byArm.get(key) ?? [];
    list.push(r);
    byArm.set(key, list);
  }

  return [...byArm.entries()].map(([arm, list]) => {
    const durs = list.map((r) => r.durationMs).filter((d): d is number => d !== null);
    const toks = list.map((r) => r.totalTokens).filter((t): t is number => t !== null);
    const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
    return {
      arm,
      runs: list.length,
      passed: list.filter((r) => r.status === 'completed').length,
      avgDurationMs: avg(durs),
      avgTokens: avg(toks),
      cacheHits: list.reduce((s, r) => s + (r.cachedSteps ?? 0), 0),
      l2Recoveries: list.reduce((s, r) => s + (r.l2RecoverySteps ?? 0), 0),
      ejections: list.reduce((s, r) => s + (r.ejectedSteps ?? 0), 0),
      agentSteps: list.reduce((s, r) => s + (r.agentSteps ?? 0), 0),
    };
  });
}

/** Render an arm's override bundle as a compact delta list. */
function ArmOverrides({ arm }: { arm: BenchmarkArm }) {
  const knobs = Object.entries(arm.knobOverrides ?? {});
  const flags = Object.entries(arm.flagOverrides ?? {});
  if (knobs.length === 0 && flags.length === 0) {
    return (
      <Text size="xs" className="text-muted-foreground">
        baseline (no overrides)
      </Text>
    );
  }
  return (
    <div className="space-y-0.5">
      {knobs.map(([k, v]) => (
        <Text key={`knob-${k}`} size="xs" className="font-mono">
          <span className="text-muted-foreground">knob</span> {k} ={' '}
          <span className="text-primary">{String(v)}</span>
        </Text>
      ))}
      {flags.map(([k, v]) => (
        <Text key={`flag-${k}`} size="xs" className="font-mono">
          <span className="text-muted-foreground">flag</span> {k} ={' '}
          <span className="text-primary">{String(v)}</span>
        </Text>
      ))}
    </div>
  );
}

export function ArmComparisonReport({ suiteId }: { suiteId: string }) {
  const query = useArmReportQuery(suiteId);

  const rows = query.data?.rows ?? [];
  const aggregates = React.useMemo(() => aggregateByArm(rows), [rows]);

  const armDefs = React.useMemo(() => {
    const meta = query.data?.run.metadata as { arms?: BenchmarkArm[] } | null;
    return meta?.arms ?? [];
  }, [query.data]);

  // Per-flow breakdown: workflow → arm → row (run 1 as representative).
  const perFlow = React.useMemo(() => {
    const map = new Map<string, Map<string, ArmComparisonRow>>();
    for (const r of rows) {
      const arm = r.arm || 'baseline';
      const flowMap = map.get(r.workflowName) ?? new Map<string, ArmComparisonRow>();
      // Prefer run 1 (uncached) as the representative for the cell.
      const existing = flowMap.get(arm);
      if (!existing || r.runNumber < existing.runNumber) flowMap.set(arm, r);
      map.set(r.workflowName, flowMap);
    }
    return [...map.entries()];
  }, [rows]);

  const armLabels = aggregates.map((a) => a.arm);

  if (query.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-8">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  if (rows.length === 0) {
    return (
      <Text className="text-muted-foreground">No arm results yet — runs may still be starting.</Text>
    );
  }

  return (
    <Box className="space-y-4">
      {/* Per-arm aggregate cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {aggregates.map((agg) => {
          const def = armDefs.find((d) => d.label === agg.arm);
          const successPct = agg.runs ? Math.round((agg.passed / agg.runs) * 100) : 0;
          return (
            <Card key={agg.arm} padding="md">
              <CardContent>
                <Flex align="center" gap="sm" className="mb-2">
                  <Badge variant="secondary">Arm {agg.arm}</Badge>
                  <Text size="sm" className="text-muted-foreground">
                    {agg.passed}/{agg.runs} passed ({successPct}%)
                  </Text>
                </Flex>
                <div className="mb-3">{def ? <ArmOverrides arm={def} /> : null}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <Metric label="Avg duration" value={formatDuration(agg.avgDurationMs)} />
                  <Metric label="Avg tokens" value={formatTokenCount(agg.avgTokens)} />
                  <Metric label="Cache hits" value={String(agg.cacheHits)} />
                  <Metric label="L2 recoveries" value={String(agg.l2Recoveries)} />
                  <Metric label="Ejections" value={String(agg.ejections)} />
                  <Metric label="Agent steps" value={String(agg.agentSteps)} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Per-flow breakdown */}
      <Card padding="md">
        <CardContent>
          <Heading level={5} className="mb-3">
            Per-flow breakdown
          </Heading>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-4 font-medium">Flow</th>
                  {armLabels.map((label) => (
                    <th key={label} className="py-1.5 pr-4 font-medium">
                      Arm {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perFlow.map(([flowName, armMap]) => (
                  <tr key={flowName} className="border-b border-border/50">
                    <td className="py-1.5 pr-4">
                      <Text size="sm" className="break-words" title={flowName}>
                        {stripBenchmarkPrefix(flowName)}
                      </Text>
                    </td>
                    {armLabels.map((label) => {
                      const cell = armMap.get(label);
                      if (!cell)
                        return (
                          <td key={label} className="py-1.5 pr-4 text-muted-foreground">
                            —
                          </td>
                        );
                      const passed = cell.status === 'completed';
                      return (
                        <td key={label} className="py-1.5 pr-4">
                          <Text size="sm">
                            <span className={passed ? 'text-success' : 'text-destructive'}>
                              {passed ? '✓' : '✗'}
                            </span>{' '}
                            {formatDuration(cell.durationMs)} · {formatTokenCount(cell.totalTokens)}
                          </Text>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <Text size="xs" className="text-muted-foreground">
        {label}
      </Text>
      <Text size="sm" weight="medium">
        {value}
      </Text>
    </div>
  );
}
